import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router';
import { LuPanelLeftClose, LuPanelLeft } from 'react-icons/lu';
import {
  IconOverview,
  IconRequirements,
  IconEpics,
  IconJira,
  IconTestCase,
  IconSuite,
  IconPlan,
  IconRun,
  IconSchedule,
  IconReports,
  IconIntelligence,
  IconEnv,
  IconData,
  IconIntegrations,
  IconRepo,
  IconAutomation,
  IconHome,
  IconProjects,
  IconSettings,
} from './NavIcons';

const isProjectPath = (pathname = '') => /\/project_\d+\//.test(pathname);

/**
 * Canonical application sidebar.
 * Workspace vs Project modes are distinct — Overview never drops into Workspace Home.
 */
const Sidebar = ({
  mode = 'workspace',
  navigateTo,
  projectId,
  projectName,
  workspaceName,
  expanded = true,
  locked = false,
  onToggleLock,
  onMouseEnter,
  onMouseLeave,
}) => {
  const location = useLocation();
  const path = location.pathname;
  const showLabels = expanded;
  const isProject = mode === 'project';
  const inProjectUrl = isProjectPath(path);

  const sections = useMemo(() => {
    if (!isProject) {
      return [
        {
          title: 'Workspace',
          items: [
            {
              id: 'home',
              label: 'Workspace Home',
              icon: IconHome,
              path: 'workspace-home',
              active: !inProjectUrl && (/\/dashboard\/?$/.test(path) || path.endsWith('/overview')),
            },
            {
              id: 'projects',
              label: 'Projects',
              icon: IconProjects,
              path: 'projects',
              active: /\/dashboard\/projects\/?$/.test(path) || path.endsWith('/dashboard/projects'),
            },
            {
              id: 'settings',
              label: 'Settings',
              icon: IconSettings,
              path: 'settings',
              active: path.includes('/settings'),
            },
          ],
        },
        {
          title: 'System',
          items: [
            {
              id: 'workspaces',
              label: 'All Workspaces',
              icon: IconProjects,
              path: 'all-workspaces',
              active: path.includes('all-workspaces'),
            },
          ],
        },
      ];
    }

    const pid = projectId || '';
    return [
      {
        title: 'Project',
        items: [
          {
            id: 'overview',
            label: 'Overview',
            icon: IconOverview,
            path: '',
            active: inProjectUrl && (/\/dashboard\/?$/.test(path) || path.endsWith('/overview')),
          },
        ],
      },
      {
        title: 'Define',
        items: [
          {
            id: 'requirements',
            label: 'Requirements',
            icon: IconRequirements,
            path: 'requirements',
            active:
              path.includes('/requirements') &&
              !path.includes('requirements/epics') &&
              !path.includes('requirements/jira'),
          },
          {
            id: 'epics',
            label: 'Epics & Stories',
            icon: IconEpics,
            path: 'requirements/epics',
            active: path.includes('requirements/epics') || path.includes('epics-stories-list'),
          },
          {
            id: 'jira',
            label: 'Jira',
            icon: IconJira,
            path: 'requirements/jira',
            active: path.includes('requirements/jira') || path.includes('jira-items'),
          },
        ],
      },
      {
        title: 'Build',
        items: [
          {
            id: 'test-cases',
            label: 'Test Cases',
            icon: IconTestCase,
            path: 'test-cases',
            active: path.includes('/test-cases'),
          },
          {
            id: 'test-suites',
            label: 'Test Suites',
            icon: IconSuite,
            path: 'test-suites',
            active: path.includes('/test-suites'),
          },
          {
            id: 'test-plans',
            label: 'Test Plans',
            icon: IconPlan,
            path: 'test-plans',
            active: path.includes('/test-plans'),
          },
        ],
      },
      {
        title: 'Run',
        items: [
          {
            id: 'executions',
            label: 'Executions',
            icon: IconRun,
            path: 'executions',
            active: path.includes('/executions'),
          },
          {
            id: 'schedules',
            label: 'Schedules',
            icon: IconSchedule,
            path: 'schedules',
            active: path.includes('schedule'),
          },
        ],
      },
      {
        title: 'Analyze',
        items: [
          {
            id: 'reports',
            label: 'Reports',
            icon: IconReports,
            path: 'reports',
            active: path.includes('/reports'),
          },
          {
            id: 'intelligence',
            label: 'AI & Learning',
            icon: IconIntelligence,
            path: 'intelligence',
            active: path.includes('/intelligence') || path.includes('/learning'),
          },
        ],
      },
      {
        title: 'Configure',
        items: [
          {
            id: 'environments',
            label: 'Environments',
            icon: IconEnv,
            path: pid ? `project-environment-list/${pid}` : 'projects',
            active: path.includes('project-environment'),
          },
          {
            id: 'test-data',
            label: 'Test Data',
            icon: IconData,
            path: pid ? `test-data-list/${pid}` : 'projects',
            active: path.includes('test-data'),
          },
          {
            id: 'integrations',
            label: 'Integrations',
            icon: IconIntegrations,
            path: 'settings?section=integrations',
            active: false,
          },
          {
            id: 'repository',
            label: 'Repository',
            icon: IconRepo,
            path: 'settings?section=integrations',
            active: false,
          },
          {
            id: 'automation',
            label: 'Automation',
            icon: IconAutomation,
            path: 'settings?section=agent-installation',
            active: false,
          },
          {
            id: 'settings',
            label: 'Settings',
            icon: IconSettings,
            path: 'settings',
            active: path.includes('/settings'),
          },
        ],
      },
      {
        title: 'Workspace',
        items: [
          {
            id: 'workspace-home',
            label: 'Workspace Home',
            icon: IconHome,
            path: 'workspace-home',
            active: false,
          },
          {
            id: 'all-projects',
            label: 'All Projects',
            icon: IconProjects,
            path: 'projects',
            active: false,
          },
          {
            id: 'all-workspaces',
            label: 'All Workspaces',
            icon: IconProjects,
            path: 'all-workspaces',
            active: false,
          },
        ],
      },
    ];
  }, [isProject, path, projectId, inProjectUrl]);

  const handleNav = (item) => {
    if (typeof navigateTo === 'function') {
      navigateTo(item.path);
    }
  };

  const contextName = isProject ? projectName || 'Project' : workspaceName || 'Workspace';
  const contextKind = isProject ? 'Project' : 'Workspace';
  const contextInitial = String(contextName).trim().charAt(0).toUpperCase() || contextKind.charAt(0);

  return (
    <aside
      className={`rt2-sidebar${expanded ? ' rt2-sidebar--expanded' : ' rt2-sidebar--collapsed'}${
        locked ? ' rt2-sidebar--locked' : ' rt2-sidebar--unlocked'
      } rt2-sidebar--${isProject ? 'project' : 'workspace'}`}
      aria-label={isProject ? 'Project navigation' : 'Workspace navigation'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="rt2-sidebar__brand">
        <div className="rt2-sidebar__context" data-tour="sidebar-context" title={`${contextKind}: ${contextName}`}>
          <span className="rt2-sidebar__context-mark" aria-hidden="true">{contextInitial}</span>
          {showLabels && (
            <span className="rt2-sidebar__context-text">
              <span className="rt2-sidebar__context-kind">
                {isProject && workspaceName ? `${workspaceName} · Project` : contextKind}
              </span>
              <span className="rt2-sidebar__context-name">{contextName}</span>
            </span>
          )}
        </div>
        {typeof onToggleLock === 'function' && (
          <button
            type="button"
            className={`rt2-sidebar__toggle${locked ? ' rt2-sidebar__toggle--active' : ''}`}
            onClick={onToggleLock}
            aria-label={locked ? 'Collapse sidebar' : 'Keep sidebar open'}
            aria-pressed={locked}
            title={locked ? 'Collapse sidebar' : 'Keep sidebar open'}
          >
            {locked ? <LuPanelLeftClose size={16} /> : <LuPanelLeft size={16} />}
          </button>
        )}
      </div>
      <div className="rt2-sidebar__scroll" data-tour="sidebar-nav">
        {sections.map((section, idx) => (
          <div
            className="rt2-sidebar__section"
            key={section.title || `sec-${idx}`}
            data-tour={section.title ? `sidebar-section-${section.title.toLowerCase()}` : undefined}
          >
            {section.title && showLabels && (
              <div className="rt2-sidebar__section-title">{section.title}</div>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              if (item.absolute) {
                return (
                  <NavLink
                    key={item.id}
                    to={item.absolute}
                    className={`rt2-nav-item${item.active ? ' rt2-nav-item--active' : ''}`}
                    title={item.label}
                    style={{ textDecoration: 'none' }}
                  >
                    <Icon />
                    {showLabels && <span>{item.label}</span>}
                  </NavLink>
                );
              }
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`rt2-nav-item${item.active ? ' rt2-nav-item--active' : ''}`}
                  onClick={() => handleNav(item)}
                  title={item.label}
                >
                  <Icon />
                  {showLabels && <span>{item.label}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </aside>
  );
};

export default Sidebar;
